import { useEffect, useMemo, useRef, useState } from "react";
import {
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Checkbox,
  Input,
  Text,
} from "@fluentui/react-components";
import { ChevronDown16Regular, Search16Regular } from "@fluentui/react-icons";
import {
  IEntityOption,
  IFilterEntityPopoverProps,
} from "./FilterEntityPopover.types";
import { useStyles } from "./FilterEntityPopover.styles";
import { PopupActionFooter } from "./PopupActionFooter";
import {
  areSelectionsEqual,
  isDateWithinRange,
} from "../../utils/filterHelpers";

const LABELS_HIDDEN_FROM_TRIGGER = ["Cloud", "360PI"];
const NONE_LABEL = "None";

export const FilterEntityPopover = ({
  label,
  options,
  isLoading,
  isError,
  selected,
  onApply,
  loadingText = "Loading...",
  allLabel = "All",
  skipAutoSelectAll = false,
  preserveOptionOrder = false,
  dateRange,
}: IFilterEntityPopoverProps): JSX.Element => {
  const styles = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [draftSelection, setDraftSelection] = useState<string[]>(selected);
  const [query, setQuery] = useState("");

  const didAutoSelect = useRef(false);
  useEffect(() => {
    if (didAutoSelect.current) return;
    if (skipAutoSelectAll) return;
    if (options.length === 0 || selected.length > 0) return;
    didAutoSelect.current = true;
    onApply(options.map((option) => option.name));
  }, [options, selected, onApply, skipAutoSelectAll]);

  const sortedOptions = useMemo(
    () =>
      preserveOptionOrder
        ? options
        : [...options].sort((a, b) => a.name.localeCompare(b.name)),
    [options, preserveOptionOrder],
  );

  // An option carrying an end date only produces data while that date sits
  // inside the selected window - the metric endpoints intersect the two filters,
  // so offering one outside the window would return an empty dashboard. Options
  // without an end date are not date-scoped and always pass.
  const dateScopedOptions = useMemo(() => {
    if (!dateRange) return sortedOptions;
    return sortedOptions.filter(
      (option) =>
        !option.endDate ||
        isDateWithinRange(option.endDate, dateRange.start, dateRange.end),
    );
  }, [sortedOptions, dateRange]);

  const filteredOptions = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle
      ? dateScopedOptions.filter((option) =>
          option.name.toLowerCase().includes(needle),
        )
      : dateScopedOptions;
  }, [dateScopedOptions, query]);

  const filteredNames = useMemo(
    () => filteredOptions.map((option) => option.name),
    [filteredOptions],
  );
  const isQueryActive = query.trim() !== "";
  const filteredAllSelected =
    filteredNames.length > 0 &&
    filteredNames.every((name) => draftSelection.includes(name));

  const handleOpenChange = (open: boolean): void => {
    if (open) {
      setDraftSelection(selected);
      setQuery("");
      setIsOpen(true);
      return;
    }
    setIsOpen(false);
  };

  const handleToggleOption = (option: IEntityOption): void => {
    setDraftSelection((current) =>
      current.includes(option.name)
        ? current.filter((name) => name !== option.name)
        : [...current, option.name],
    );
  };

  const handleToggleSelectAll = (): void => {
    const outsideFilter = draftSelection.filter(
      (name) => !filteredNames.includes(name),
    );
    setDraftSelection(
      filteredAllSelected
        ? outsideFilter
        : [...outsideFilter, ...filteredNames],
    );
  };

  const handleApply = (): void => {
    if (!areSelectionsEqual(draftSelection, selected)) {
      onApply(draftSelection);
    }
    setIsOpen(false);
  };

  const handleCancel = (): void => {
    setDraftSelection(selected);
    setIsOpen(false);
  };

  // Date-scoped options (sprints) can drop out of the list when the window
  // moves while their names stay in `selected`, so All/None is measured against
  // the options actually on offer rather than against the raw selection.
  const visibleSelection = useMemo(
    () =>
      dateScopedOptions
        .map((option) => option.name)
        .filter((name) => selected.includes(name)),
    [dateScopedOptions, selected],
  );
  const allSelected =
    dateScopedOptions.length > 0 &&
    visibleSelection.length === dateScopedOptions.length;
  const labelledSelection = useMemo(
    () =>
      visibleSelection.filter(
        (name) => !LABELS_HIDDEN_FROM_TRIGGER.includes(name),
      ),
    [visibleSelection],
  );
  const selectionText = allSelected
    ? allLabel
    : labelledSelection.length > 0
      ? labelledSelection.join(", ")
      : NONE_LABEL;
  const triggerText = isLoading ? loadingText : selectionText;

  const selectAllLabel = isQueryActive
    ? filteredAllSelected
      ? `Deselect All (${filteredOptions.length} matching)`
      : `Select All (${filteredOptions.length} matching)`
    : filteredAllSelected
      ? "Deselect All"
      : "Select All";

  if (isError) {
    return <Text role="alert">Failed to load {label.toLowerCase()}s</Text>;
  }

  return (
    <Popover
      open={isOpen}
      onOpenChange={(_e, data) => handleOpenChange(data.open)}
      trapFocus
      withArrow
      positioning="below"
    >
      <PopoverTrigger disableButtonEnhancement>
        <button
          type="button"
          className={styles.trigger}
          disabled={isLoading}
          aria-label={`${label}: ${triggerText}`}
        >
          <span className={styles.triggerLabel}>{triggerText}</span>
          <ChevronDown16Regular
            className={styles.triggerIcon}
            aria-hidden="true"
          />
        </button>
      </PopoverTrigger>
      <PopoverSurface className={styles.surface}>
        <div className={styles.searchRow}>
          <Input
            className={styles.search}
            contentBefore={<Search16Regular className={styles.searchIcon} />}
            value={query}
            onChange={(_e, data) => setQuery(data.value)}
            placeholder={`Search ${label.toLowerCase()}s`}
            aria-label={`Search ${label.toLowerCase()}s`}
          />
        </div>
        <div
          className={styles.list}
          role="group"
          aria-label={`${label} options`}
        >
          {filteredOptions.length === 0 ? (
            <div role="status" className={styles.noMatches}>
              No matches
            </div>
          ) : (
            <>
              <Checkbox
                className={styles.option}
                checked={filteredAllSelected}
                onChange={handleToggleSelectAll}
                label={selectAllLabel}
              />
              {filteredOptions.map((option) => (
                <Checkbox
                  key={option.id}
                  className={styles.option}
                  checked={draftSelection.includes(option.name)}
                  onChange={() => handleToggleOption(option)}
                  label={option.name}
                />
              ))}
            </>
          )}
        </div>
        <PopupActionFooter onApply={handleApply} onCancel={handleCancel} />
      </PopoverSurface>
    </Popover>
  );
};
